/**
 * What the feedback card says (TASKS T56, BRIEF <spanish_correctness> layer 2, SPEC §1 step 5).
 *
 * Three rules from the brief, and every one of them is a rule about *wording*, which is why this is
 * a pure function with a fixture test rather than a template inside a card:
 *
 *  1. **Name the error type.** Never a bare "wrong". `wrong-gender`, `wrong-person`,
 *     `missing-accent`, `ser-estar` — the classification `lang/nearmiss.ts` already produced is
 *     turned into a heading the learner reads, so he knows *what kind* of thing he got wrong. An
 *     adult analytical learner can act on "right tense, wrong person"; he can do nothing at all with
 *     "incorrect".
 *  2. **Always show the correctly accented form.** On a right answer too. Seeing the correct
 *     spelling after a correct answer is free reinforcement and costs nothing.
 *  3. **Flag a missing accent explicitly.** On its own line, and *while the answer still counts as
 *     correct* — scoring is accent-insensitive (BRIEF), so this is the only place the learner is
 *     ever told. Folded into a general "close" message it would be invisible.
 *
 * What is *not* here: any judgement of the person, any encouragement, any count of how many times
 * this item has beaten him, and anything that could be read as a score. "Not yet" is the strongest
 * thing this card says.
 */

import type { NearMissKind } from '../lang/nearmiss';
import type { Grade } from '../lang/validator';
import { previousEncoding } from './encoding';
import type { ItemRecord } from '../store/schema';

/**
 * The heading for each error class: two or three words, as a *name*.
 *
 * Distinct from `nearmiss.ts`'s `NEAR_MISS_LABELS`, which are sentences. A heading is scanned in the
 * dark with one thumb; a sentence is read. Both exist because they are doing different jobs, and the
 * sentence — which reaches this module inside `Grade.message` — is still shown underneath.
 */
export const ERROR_HEADINGS: Readonly<Record<NearMissKind, string>> = {
  'missing-accent': 'Missing accent',
  'wrong-accent': 'Accent misplaced',
  'wrong-gender': 'Wrong gender',
  'wrong-number': 'Wrong number',
  'wrong-person': 'Wrong person',
  'wrong-tense': 'Wrong tense',
  'ser-estar': 'ser / estar',
  spelling: 'Spelling',
  unrelated: 'Not this one',
};

export interface FeedbackView {
  /** The verdict, and the strongest thing this card says. Never "wrong", never "fail". */
  verdict: 'Correct' | 'Not yet';
  correct: boolean;
  /** ALWAYS the correctly accented expected form, right answer or not. */
  correctForm: string;
  /** What the learner actually produced. Null when it adds nothing — an empty or identical answer. */
  yours: string | null;
  /** The error class as a heading. Null on a clean correct answer. */
  errorHeading: string | null;
  /**
   * The sentence naming what happened. Never a bare "wrong".
   *
   * Null when `accentNote` already says it. The validator's message and the accent line were both on
   * screen at once, one under the other, in slightly different words — which reads less like
   * thoroughness and more like the app not knowing what it had already told him.
   */
  message: string | null;
  /**
   * The explicit accent flag, on its own line (brief rule 3).
   *
   * Present on an accent-only miss, which *scores as correct* — so this line is the only notice the
   * learner ever gets that his spelling was not the spelling.
   */
  accentNote: string | null;
  /** What he wrote about this item the last time it beat him. Only on a failure (D-140). */
  encoding: string | null;
}

export interface FeedbackInput {
  grade: Grade;
  /** The item as banked, so the encoding line is the one that is actually on disk. */
  record: ItemRecord;
  /** What the learner submitted. */
  answer: string;
}

export function feedbackView(input: FeedbackInput): FeedbackView {
  const { grade, record } = input;
  const answer = input.answer.trim();
  const kind = grade.nearMiss;
  const note = accentNote(kind, grade.correctForm, answer);

  return {
    verdict: grade.correct ? 'Correct' : 'Not yet',
    correct: grade.correct,
    correctForm: grade.correctForm,
    // Shown only when it is different and there is something to show. Echoing an identical string
    // back reads as the card not having understood, and an empty one is "no sé" — already said.
    yours: answer.length > 0 && answer !== grade.correctForm ? answer : null,
    errorHeading: kind === null ? null : ERROR_HEADINGS[kind],
    message: note === null ? grade.message : null,
    accentNote: note,
    // Only on a failure: on every correct card too and it becomes wallpaper (D-140).
    encoding: grade.correct ? null : previousEncoding(record),
  };
}

/**
 * The explicit accent line.
 *
 * Spelled out as a comparison rather than as "watch your accents", because the learner has to be
 * able to see *which* letter — at minimum brightness, with one thumb, having just been told he was
 * right. The word "correct" does not appear: the answer was correct, and the accent is a separate
 * fact about the spelling.
 */
function accentNote(kind: NearMissKind | null, expected: string, actual: string): string | null {
  if (kind !== 'missing-accent' && kind !== 'wrong-accent') return null;
  if (actual.length === 0) return null;
  const what = kind === 'missing-accent' ? 'The accent is missing' : 'The accent is misplaced';
  return `${what}: ${expected}, not ${actual}.`;
}
